import {
  getTournamentSyncPreview,
  commitTournamentSync,
} from "../../../api/tournamentSync.api";
import { seasonSpan, seasonYear } from "../../../utils/tournamentGroups";

/* ── Tournament scope ──────────────────────────────────────────────
   Every season of an ingested tournament is its own Competition, and
   the ingest mints one archival Player per season — so "V Kohli" is 19
   rows across IPL 2008-2026. This scope loads each season's preview
   with the existing per-competition endpoint, folds the same player
   across seasons into ONE row, and on Save fans that one choice back
   out to every season's archival row via the per-competition commit.

   Per season, not one big request: a never-synced season's preview
   takes ~5 s, so 19 of them in one call would outlive the 30 s axios
   and 60 s CloudFront timeouts. Two at a time keeps gunicorn workers
   free for everyone else.                                            */

const PREVIEW_CONCURRENCY = 2;
export const CONFIDENT_TAGS = new Set(["linked", "auto_from_ci_id", "auto_from_alias", "high_confidence"]);
const TAG_ORDER = ["linked", "auto_from_ci_id", "auto_from_alias", "high_confidence", "needs_review", "no_match"];

const errorText = (e) =>
  e?.response?.data?.detail ||
  (e?.code === "ECONNABORTED" ? "timed out" : "") ||
  e?.message ||
  "failed";

const normName = (s) => String(s || "").toLowerCase().replace(/[^a-z0-9]+/g, " ").trim();

function splitBy(list, keyOf) {
  const m = new Map();
  for (const x of list) {
    const k = keyOf(x);
    if (!m.has(k)) m.set(k, []);
    m.get(k).push(x);
  }
  return [...m.entries()];
}

function mostCommon(values) {
  const counts = new Map();
  for (const v of values) counts.set(v, (counts.get(v) || 0) + 1);
  return [...counts.entries()].sort((a, b) => b[1] - a[1])[0]?.[0];
}

export async function loadTournamentPreview(idsParam, { name, onProgress } = {}) {
  const ids = String(idsParam || "").split(",").filter(Boolean);
  const loaded = new Array(ids.length);
  const failed = [];
  let next = 0;
  let done = 0;
  onProgress?.({ done, total: ids.length });
  async function worker() {
    while (next < ids.length) {
      const i = next++;
      try {
        loaded[i] = (await getTournamentSyncPreview(ids[i])).data;
      } catch (e) {
        failed.push({ id: ids[i], detail: errorText(e) });
      }
      done += 1;
      onProgress?.({ done, total: ids.length });
    }
  }
  await Promise.all(
    Array.from({ length: Math.min(PREVIEW_CONCURRENCY, ids.length) }, worker),
  );
  return { data: buildTournamentPreview(name, loaded.filter(Boolean), failed) };
}

export function buildTournamentPreview(name, seasonPayloads, failed) {
  const entries = [];
  const seasons = [];
  for (const p of seasonPayloads) {
    const c = p.competition || {};
    const season = {
      id: c.id,
      name: c.name,
      year: seasonYear(c.name) ?? seasonYear(c.season),
      match_count: c.match_count || 0,
      source: c.source,
      match_type_code: c.match_type_code,
    };
    seasons.push(season);
    for (const row of p.rows || []) entries.push({ row, season });
  }
  seasons.sort((a, b) => (a.year ?? 0) - (b.year ?? 0));

  // One bucket per person. Same normalised name = same person, EXCEPT
  // when the evidence says otherwise:
  //   * two different source Cricinfo ids → split by id;
  //   * the name appears twice in ONE season → namesakes in the same
  //     tournament; split by team so neither gets the other's link.
  const buckets = [];
  for (const [nk, list] of splitBy(entries, (e) => normName(e.row.archival.name) || e.row.archival.id)) {
    const ciIds = new Set(list.map((e) => e.row.archival.ci_player_id).filter(Boolean));
    const parts = ciIds.size > 1
      ? splitBy(list, (e) => e.row.archival.ci_player_id || "")
      : [["", list]];
    for (const [ck, part] of parts) {
      const perSeason = new Set();
      let repeated = false;
      for (const e of part) {
        if (perSeason.has(e.season.id)) repeated = true;
        perSeason.add(e.season.id);
      }
      if (repeated) {
        for (const [tk, sub] of splitBy(part, (e) => e.row.archival.team_name || "")) {
          buckets.push({ key: `${nk}|${ck}|${tk}`, members: sub, split: true });
        }
      } else {
        buckets.push({ key: `${nk}|${ck}`, members: part, split: ciIds.size > 1 });
      }
    }
  }

  const rows = buckets.map(tournamentRow);
  const sources = [...new Set(seasons.map((s) => s.source).filter(Boolean))];
  return {
    tournament: {
      name: name || seasons[0]?.name || "Tournament",
      season_count: seasons.length,
      season_span: seasonSpan(seasons),
      match_count: seasons.reduce((t, s) => t + s.match_count, 0),
      match_type_code: mostCommon(seasons.map((s) => s.match_type_code).filter(Boolean)) || "",
      source: sources.join(" / "),
      archival_player_count: rows.length,
      archival_row_count: entries.length,
      failed,
    },
    rows,
  };
}

function tournamentRow({ key, members, split }) {
  const ms = [...members].sort((a, b) => (a.season.year ?? 0) - (b.season.year ?? 0));
  const sum = (f) => ms.reduce((t, e) => t + (e.row.archival[f] || 0), 0);
  const teams = [...new Set(ms.map((e) => e.row.archival.team_name).filter(Boolean))];
  const ciIds = [...new Set(ms.map((e) => e.row.archival.ci_player_id).filter(Boolean))];

  // Best score per canonical across every season's suggestions.
  const byId = new Map();
  for (const e of ms) {
    for (const s of e.row.suggestions || []) {
      const prev = byId.get(s.id);
      if (!prev || s.score > prev.score) byId.set(s.id, s);
    }
  }
  let suggestions = [...byId.values()].sort((a, b) => b.score - a.score);

  // The seasons that already know who this is (Cricinfo id, a prior
  // sync's alias, a decisive fuzzy match). If they agree, that answer
  // applies to the seasons that don't know yet — that is the point of
  // syncing a tournament at once. If they disagree, a human decides.
  const confident = ms.filter((e) => CONFIDENT_TAGS.has(e.row.tag) && e.row.suggestions?.[0]);
  const picks = [...new Set(confident.map((e) => e.row.suggestions[0].id))];
  let tag;
  let conflict = false;
  // Seasons an earlier sync already linked. When every season is linked
  // to the same player the row is done. When only some are, the link is
  // the answer for the rest: tag it "known" so Save carries it to them
  // (the server reports the already-linked seasons as unchanged).
  const linkedIds = [...new Set(ms.map((e) => e.row.archival.canonical?.id).filter(Boolean))];
  const allLinked = ms.every((e) => e.row.archival.canonical);
  let canonical = null;
  if (picks.length === 1) {
    if (allLinked && linkedIds.length === 1) {
      tag = "linked";
      canonical = ms[0].row.archival.canonical;
    } else {
      const unlinked = TAG_ORDER.find((t) => t !== "linked" && confident.some((e) => e.row.tag === t));
      tag = unlinked || "auto_from_alias";
    }
    suggestions = [byId.get(picks[0]), ...suggestions.filter((s) => s.id !== picks[0])];
  } else if (picks.length > 1) {
    tag = "needs_review";
    conflict = true;
    suggestions = [
      ...picks.map((id) => byId.get(id)),
      ...suggestions.filter((s) => !picks.includes(s.id)),
    ];
  } else {
    tag = suggestions.length ? "needs_review" : "no_match";
  }

  return {
    archival: {
      id: `t:${key}`,
      name: mostCommon(ms.map((e) => e.row.archival.name)),
      ci_player_id: ciIds.length === 1 ? ciIds[0] : "",
      runs: sum("runs"),
      balls_faced: sum("balls_faced"),
      balls_bowled: sum("balls_bowled"),
      wickets: sum("wickets"),
      team_id: null,
      team_name: teams.slice(0, 3).join(" · ") + (teams.length > 3 ? ` +${teams.length - 3}` : ""),
      canonical,
    },
    suggestions: suggestions.slice(0, 8),
    tag,
    conflict,
    split,
    members: ms.map((e) => ({
      archival_id: e.row.archival.id,
      season: e.season,
      team_name: e.row.archival.team_name,
      tag: e.row.tag,
      top: e.row.suggestions?.[0]?.name || "",
    })),
  };
}

// Rows per commit request. A data-mode merge repoints ~30 tables per
// row, so a whole season in one request could outlive the 30 s axios and
// 60 s CloudFront limits; the server would finish the work while the page
// reported a failure. Batches keep every request short in both modes.
export const COMMIT_CHUNK = 40;

const emptyResult = (mode) => ({
  mode, merged: 0, linked: 0, unchanged: 0, unlinked: 0, skipped: 0, aliases_written: 0,
  totals: { moved: 0, deleted: 0, linked: 0 }, errors: [],
});

function addResult(agg, data, label = "") {
  for (const k of ["merged", "linked", "unchanged", "unlinked", "skipped", "aliases_written"]) {
    agg[k] += data[k] || 0;
  }
  for (const k of Object.keys(agg.totals)) agg.totals[k] += data.totals?.[k] || 0;
  for (const err of data.errors || []) {
    agg.errors.push(label ? { ...err, detail: `${label}: ${err.detail}` } : err);
  }
}

// Commit `mappings` for one competition or match in batches, summing the
// results. A batch that fails outright is reported per row it carried and
// the remaining batches still go through, since every batch is its own
// transaction on the server.
export async function commitInChunks(commitFn, scopeId, mappings, mode, { label = "", onChunk } = {}) {
  const agg = emptyResult(mode);
  for (let i = 0; i < mappings.length; i += COMMIT_CHUNK) {
    const part = mappings.slice(i, i + COMMIT_CHUNK);
    try {
      const { data } = await commitFn(scopeId, part, mode);
      addResult(agg, data, label);
    } catch (e) {
      agg.errors.push({
        detail: `${label ? `${label}: ` : ""}${part.length} row${part.length === 1 ? "" : "s"} not saved (${errorText(e)})`,
      });
    }
    onChunk?.(Math.min(i + COMMIT_CHUNK, mappings.length), mappings.length);
  }
  return agg;
}

// One Save → one commit per season, in turn. Each season's commit gets
// only its own archival rows, so the per-competition alias/team logic
// on the server is exactly what a season-by-season sync would run. A
// season that fails is reported and the rest still go through.
export async function commitTournament(mappings, mode, { preview, onProgress } = {}) {
  const rowById = new Map((preview?.rows || []).map((r) => [r.archival.id, r]));
  const bySeason = new Map();
  for (const { archival_id, canonical_id, unlink } of mappings) {
    for (const m of rowById.get(archival_id)?.members || []) {
      if (!bySeason.has(m.season.id)) bySeason.set(m.season.id, { season: m.season, mappings: [] });
      bySeason.get(m.season.id).mappings.push(
        unlink ? { archival_id: m.archival_id, unlink: true } : { archival_id: m.archival_id, canonical_id },
      );
    }
  }
  const agg = { ...emptyResult(mode), seasons_committed: 0 };
  let done = 0;
  for (const { season, mappings: ms } of bySeason.values()) {
    onProgress?.({ done, total: bySeason.size });
    const data = await commitInChunks(commitTournamentSync, season.id, ms, mode, { label: season.name });
    addResult(agg, data);
    agg.seasons_committed += 1;
    done += 1;
  }
  onProgress?.({ done, total: bySeason.size });
  return { data: agg };
}
